import { addDays, startOfCalendarWeek } from '~/calculations/calendar'
import type { WeekStartDay } from '~/models/settings'
import type { CalendarDate } from '~/types/dates'

/**
 * Observation scope and privacy selection for an AI-review export.
 *
 * Every default here is the cautious one, and that is a decision, not an
 * accident (D-019): the default scope is the current week plus the previous
 * four, and photos, screenshots, personal notes and exact dates are all OFF.
 * A user who wants to disclose more says so explicitly.
 *
 * These defaults are asserted by test, because "off by default" is the kind of
 * property that quietly inverts during a refactor.
 */

export type ObservationScope = 'currentWeek' | 'currentPlusPreviousFour' | 'fullHistory'

export interface PrivacySelection {
  readonly scope: ObservationScope
  readonly includePhotos: boolean
  readonly includeStrengthScreenshots: boolean
  readonly includeNotes: boolean
  readonly includeExactDates: boolean
}

export const DEFAULT_PRIVACY_SELECTION: PrivacySelection = {
  scope: 'currentPlusPreviousFour',
  includePhotos: false,
  includeStrengthScreenshots: false,
  includeNotes: false,
  includeExactDates: false,
}

/** Number of whole calendar weeks each scope covers, current week included. */
const WEEKS_IN_SCOPE: Readonly<Record<Exclude<ObservationScope, 'fullHistory'>, number>> = {
  currentWeek: 1,
  currentPlusPreviousFour: 5,
}

export interface ReportingPeriod {
  readonly from: CalendarDate | undefined
  readonly to: CalendarDate
  readonly scope: ObservationScope
  readonly weekStartDay: WeekStartDay
}

/**
 * The window an export covers, aligned to calendar weeks.
 *
 * Calendar weeks, not trailing days: export scope is one of the three things
 * D-034 says calendar weeks govern, and they start on the user's configured
 * `weekStartDay`. `from` is undefined only for full history, where there is no
 * lower bound to state.
 */
export function reportingPeriodFor(
  reference: CalendarDate,
  selection: PrivacySelection,
  weekStartDay: WeekStartDay,
): ReportingPeriod {
  if (selection.scope === 'fullHistory') {
    return { from: undefined, to: reference, scope: selection.scope, weekStartDay }
  }

  const currentWeekStart = startOfCalendarWeek(reference, weekStartDay)
  const weeks = WEEKS_IN_SCOPE[selection.scope]

  return {
    from: addDays(currentWeekStart, -7 * (weeks - 1)),
    to: reference,
    scope: selection.scope,
    weekStartDay,
  }
}

/** True when a date falls inside the reporting period. */
export function isInPeriod(date: CalendarDate, period: ReportingPeriod): boolean {
  if (date > period.to) return false
  return period.from === undefined || date >= period.from
}
